import { useCallback, useEffect, useState } from 'react';
import { api } from '../lib/api.ts';
import { baht, clock, thaiDate } from '../lib/format.ts';
import { openDrawer } from '../actions.ts';
import { useStore } from '../store.tsx';
import { Alert, AsyncButton, Field, Modal, NumPad, padToSatang, useToast } from '../ui.tsx';
import { CloseDrawerForm } from './CloseDrawerForm.tsx';
import type { CashMovement, ExpenseItem } from '../lib/types.ts';

type MovementType = CashMovement['movementType'];

const TYPES: Array<[MovementType, string, string]> = [
  ['expense', 'ค่าใช้จ่าย', 'จ่ายออกจริง เช่น ซื้อของ ค่าแรง'],
  ['bank_deposit', 'ฝากธนาคาร', 'ย้ายเงินออกไปเข้าบัญชี ไม่ใช่ค่าใช้จ่าย'],
  ['float_out', 'เบิกเงินออก', 'เอาเงินออกจากลิ้นชักโดยยังไม่ใช่รายจ่าย'],
  ['float_in', 'เติมเงินทอน', 'ใส่เงินเพิ่มเข้าลิ้นชักระหว่างวัน'],
];

/**
 * เงินเข้าออกลิ้นชักและการปิดรอบ
 *
 * ★ แยกค่าใช้จ่ายจริงออกจากการย้ายเงิน ★
 * ระบบเดิมปนกันหมดในช่องข้อความอิสระ ทำให้ยอดค่าใช้จ่ายรวมการฝากธนาคาร
 * และการเก็บเงินเข้าไปด้วย เกินจริงไป 308,768 บาท
 *
 * ที่นี่ประเภทเป็นตัวเลือกตายตัว 4 แบบ และมีแต่ "ค่าใช้จ่าย" เท่านั้นที่มีหมวด
 */
export function DrawerScreen({ onClosed }: { onClosed: () => void }) {
  const store = useStore();
  const toast = useToast();

  const [movements, setMovements] = useState<CashMovement[]>([]);
  const [netSatang, setNetSatang] = useState(0);
  const [showAdd, setShowAdd] = useState(false);
  const [showClose, setShowClose] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const drawer = store.drawer;

  const load = useCallback(async () => {
    if (!drawer) return;
    try {
      const result = await api.get<{ cashMovements: CashMovement[]; netSatang: number }>(
        '/cash-movements',
        { drawerSessionId: drawer.id },
      );
      setMovements(result.cashMovements);
      setNetSatang(result.netSatang);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'โหลดรายการเงินเข้าออกไม่สำเร็จ');
    }
  }, [drawer]);

  useEffect(() => { void load(); }, [load]);

  if (!drawer) return <div className="page"><Alert>ยังไม่ได้เปิดรอบลิ้นชัก</Alert></div>;

  const typeName = (t: MovementType) => TYPES.find(([v]) => v === t)?.[1] ?? t;

  return (
    <div className="pane">
      <div className="page" style={{ padding: 0, maxWidth: 720 }}>
        <h1>ลิ้นชักเงินสด</h1>
        <p className="lede">
          {store.branch.name} · วันทำการ {thaiDate(drawer.businessDate)} · {drawer.docNo}
        </p>

        {error && <Alert>{error}</Alert>}

        <div className="rowitem" style={{ marginBottom: 14 }}>
          <span className="grow">เงินทอนตั้งต้น</span>
          <span className="v">{baht(drawer.openingFloatSatang)}</span>
        </div>
        <div className="rowitem">
          <span className="grow">เงินเข้าออกสุทธิระหว่างวัน</span>
          <span className="v">{baht(netSatang)}</span>
        </div>

        {/* ★ ไม่แสดงยอดขายของวันที่นี่ ★ ตามที่ตกลงกันไว้
            พนักงานไม่ต้องรู้ว่าวันนี้ขายไปแล้วเท่าไร ยอดที่ควรมีในลิ้นชัก
            จะเห็นตอนกดปิดรอบ ซึ่งเป็นตอนที่จำเป็นต้องใช้จริง */}

        <div className="action-row" style={{ margin: '18px 0 26px' }}>
          <button className="btn" onClick={() => setShowAdd(true)}>+ บันทึกเงินเข้าออก</button>
          <button className="btn ghost" onClick={() => setShowClose(true)}>ปิดรอบและนับเงิน</button>
        </div>

        <h2>รายการวันนี้</h2>
        {movements.length === 0 ? (
          <div className="empty">ยังไม่มีเงินเข้าออกลิ้นชักวันนี้</div>
        ) : (
          <div className="rowlist">
            {movements.map((m) => (
              <div key={m.id} className="rowitem">
                <span className="grow">
                  <strong>{m.detail || m.itemName || typeName(m.movementType)}</strong>
                  <div className="sub">
                    {typeName(m.movementType)}
                    {m.categoryName && ` · ${m.categoryName}`}
                    {' · '}{clock(m.createdAt)} · {m.docNo}
                  </div>
                </span>
                <span
                  className="v"
                  style={{ color: m.amountSatang < 0 ? 'var(--crit)' : 'var(--ok)' }}
                >
                  {baht(m.amountSatang)}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {showAdd && (
        <Modal title="บันทึกเงินเข้าออกลิ้นชัก" onClose={() => setShowAdd(false)}>
          <MovementForm
            drawerSessionId={drawer.id}
            onDone={async () => {
              setShowAdd(false);
              await load();
              toast('บันทึกแล้ว', 'ok');
            }}
            onError={(m) => toast(m, 'crit')}
          />
        </Modal>
      )}

      {showClose && (
        <Modal title="ปิดรอบลิ้นชัก" onClose={() => setShowClose(false)} wide>
          <CloseDrawerForm
            drawer={drawer}
            onCancel={() => setShowClose(false)}
            onClosed={async () => {
              setShowClose(false);
              await store.reloadDrawer();
              onClosed();
            }}
          />
        </Modal>
      )}
    </div>
  );
}

/* ══ ฟอร์มบันทึกเงินเข้าออก ═══════════════════════════════ */

function MovementForm({
  drawerSessionId,
  onDone,
  onError,
}: {
  drawerSessionId: number;
  onDone: () => void | Promise<void>;
  onError: (message: string) => void;
}) {
  const store = useStore();
  const [type, setType] = useState<MovementType>('expense');
  const [categoryId, setCategoryId] = useState<number | null>(
    store.expenseCategories[0]?.id ?? null,
  );
  const [items, setItems] = useState<ExpenseItem[]>([]);
  const [itemId, setItemId] = useState<number | null>(null);
  const [detail, setDetail] = useState('');
  const [amount, setAmount] = useState('');

  // ★ รายการย่อยที่ใช้บ่อยขึ้นก่อน ★ ทำให้ "อื่น ๆ" ไม่บวมจนสรุปอะไรไม่ได้
  useEffect(() => {
    if (type !== 'expense' || categoryId === null) {
      setItems([]);
      return;
    }
    api
      .get<{ expenseItems: ExpenseItem[] }>('/expense-items', { categoryId, limit: 12 })
      .then((r) => setItems(r.expenseItems))
      .catch(() => setItems([]));
  }, [type, categoryId]);

  const category = store.expenseCategories.find((c) => c.id === categoryId);
  const amountSatang = padToSatang(amount);
  const needsDetail = type === 'expense' && category?.requiresDetail === true;

  const ready =
    amountSatang !== null &&
    amountSatang > 0 &&
    (type !== 'expense' || categoryId !== null) &&
    (!needsDetail || detail.trim() !== '');

  const submit = async () => {
    try {
      const result = await api.post<{ shouldOpenDrawer: boolean; drawerReason: string }>(
        '/cash-movements',
        {
          drawerSessionId,
          movementType: type,
          expenseCategoryId: type === 'expense' ? categoryId : null,
          expenseItemId: type === 'expense' ? itemId : null,
          detail: detail.trim(),
          amountSatang: amountSatang!,
        },
      );
      // หยิบเงินเข้าหรือออกจากลิ้นชัก ต้องเปิดลิ้นชักเสมอ
      await openDrawer(store, result.shouldOpenDrawer, result.drawerReason);
      await onDone();
    } catch (err) {
      onError(err instanceof Error ? err.message : 'บันทึกไม่สำเร็จ');
    }
  };

  return (
    <>
      <Field label="ประเภท">
        <div className="pick-grid">
          {TYPES.map(([value, name]) => (
            <button
              key={value}
              className="pick"
              aria-pressed={type === value}
              onClick={() => setType(value)}
            >
              {name}
            </button>
          ))}
        </div>
        <div style={{ marginTop: 8, color: 'var(--ink-3)', fontSize: '.88rem' }}>
          {TYPES.find(([v]) => v === type)?.[2]}
        </div>
      </Field>

      {type === 'expense' && (
        <>
          <Field label="หมวด">
            <div className="pick-grid">
              {store.expenseCategories.map((c) => (
                <button
                  key={c.id}
                  className="pick"
                  aria-pressed={categoryId === c.id}
                  onClick={() => {
                    setCategoryId(c.id);
                    setItemId(null);
                  }}
                >
                  {c.name}
                </button>
              ))}
            </div>
          </Field>

          {items.length > 0 && (
            <Field label="รายการที่ใช้บ่อย">
              <div className="pick-grid">
                {items.map((item) => (
                  <button
                    key={item.id}
                    className="pick"
                    aria-pressed={itemId === item.id}
                    onClick={() => {
                      setItemId(item.id === itemId ? null : item.id);
                      if (item.id !== itemId && detail === '') setDetail(item.name);
                    }}
                  >
                    {item.name}
                  </button>
                ))}
              </div>
            </Field>
          )}
        </>
      )}

      <Field label={needsDetail ? 'จ่ายอะไร (ต้องกรอก)' : 'รายละเอียด'}>
        <input
          value={detail}
          onChange={(e) => setDetail(e.target.value)}
          placeholder={needsDetail ? 'หมวดนี้ต้องระบุว่าจ่ายอะไร' : 'ไม่บังคับ'}
        />
      </Field>

      <Field label="จำนวนเงิน (บาท)">
        <div className="amount-display">{baht(amountSatang ?? 0)}</div>
        <NumPad onChange={setAmount} />
      </Field>

      <AsyncButton className="btn big wide" onClick={submit} disabled={!ready}>
        บันทึก
      </AsyncButton>
    </>
  );
}
